/**
 * Three daily jobs off the real work of a banquet and conference floor, each
 * a card on the board like any room and each asked for by somebody: walk up
 * to them while it is posted and they hand it to you (`sim/jobs.js`).
 *
 *   flip      the ballroom between two events: the lunch is still standing,
 *             in the wrong colour and the wrong place, and the dinner's sheet
 *             wants it moved over, re-clothed and re-chaired. Everything the
 *             floor guide already reads a room for (a round off the plan, a
 *             wrong cloth, a round with too many chairs) is the whole job
 *   refresh   a meeting gone to its break: the room is set, but two cloths
 *             have gone to the wash, a round has lost chairs to the corners,
 *             and there are glasses on the floor. Put it back and carry the
 *             glasses out to the kitchen pass before the session comes back
 *   coffee    a coffee break by the grand entrance: two stations dressed in
 *             white, no chairs, and the urn carried up from the kitchen pass
 *
 * Who asks is on their own row (`jobs` in `data/staff.js`), so nothing here
 * names anybody. Times are minutes from the shift's start, so a job lands in
 * the same place on a morning, an afternoon and a double. None of them ghost:
 * a job nobody took is a job somebody else did, and costs you nothing.
 *
 * The ballroom's anchors are 12 ft apart from (26, 28), six by six, and the
 * dance floor is columns two and three of rows three to five; every layout
 * here is off it (`jobs.test.js`).
 */
export const JOBS = Object.freeze([
  {
    id: 'flip',
    code: 'FLIP',
    title: 'Room Flip',
    owner: 'banquets',
    posts: { at: 90, due: 240 },
    award: { banquets: 14 },
    note: 'Lunch is out and the dinner is in. Same room, different sheet. Turn it.',
    // What the lunch left standing.
    before: { layout: { col: 0, row: 0, cols: 3, rows: 2 }, linen: 'burgundy', chairs: 10 },
    setup: {
      tableType: 'round',
      tableCount: 6,
      chairsPerTable: 8,
      linen: 'white',
      layout: { col: 1, row: 0, cols: 3, rows: 2 },
      staging: ['Dance floor to remain clear'],
    },
    estimate: 120,
  },
  {
    id: 'refresh',
    code: 'RFSH',
    title: 'Mid-day Refresh',
    owner: 'banquets',
    posts: { at: 30, due: 170 },
    award: { banquets: 10 },
    note: 'They are on their break. Room back the way it was before they are back.',
    // Set to the sheet, then: two cloths gone, a round short of chairs, glasses down.
    before: { layout: 'setup', stripped: 2, missing: 3, litter: { kind: 'wineglass', count: 6 } },
    setup: {
      tableType: 'round',
      tableCount: 6,
      chairsPerTable: 8,
      linen: 'ivory',
      layout: { col: 3, row: 0, cols: 3, rows: 2 },
      staging: ['Dance floor to remain clear'],
    },
    // Glasses go out to the bus tub at the kitchen pass, in feet.
    clearTo: [-18, -5.5],
    estimate: 80,
  },
  {
    id: 'coffee',
    code: 'BRK',
    title: 'Coffee Break',
    owner: 'kitchen',
    posts: { at: 75, due: 195 },
    award: { banquets: 6, kitchen: 8 },
    note: 'Two stations by the grand entrance and the urn from the pass. Coffee at the top of the hour.',
    before: { layout: null, bring: { kind: 'coffeeurn', count: 1, from: [-18, -5.5] } },
    setup: {
      tableType: 'round',
      tableCount: 2,
      chairsPerTable: 0,
      linen: 'white',
      layout: { col: 5, row: 0, cols: 1, rows: 2 },
      staging: ['Stations by the grand entrance'],
    },
    estimate: 60,
  },
]);

export const JOBS_BY_ID = Object.freeze(Object.fromEntries(JOBS.map((row) => [row.id, row])));

export default JOBS;
